import {
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    SaveResponse,
    isValidAppType,
    AppType,
    InstructionLabel,
    Locale,
    QuestionDetails
} from "./models";
import { appInfo, backendUri, getAuthLabel, jwtToken, supportEnvHost, userInfo } from "../authConfig";
import { jwtDecode } from "jwt-decode";
import { diffWords } from "diff";
import DOMPurify from "dompurify";
type TokenValues = {
    exp: number;
    isInchargeOfAiWorks: string;
    user_name: string;
};
const BACKEND_URI = backendUri ?? "";

// Suppress fetch error alerts during browser refresh / navigation.
// Use `pagehide` (non-cancelable) instead of `beforeunload` so that a
// canceled beforeunload (e.g. Chat.tsx calling event.preventDefault()) cannot
// leave `isPageUnloading` permanently stuck at `true` for the session.
// Guard `window` access to avoid import-time crashes in non-browser contexts.
let isPageUnloading = false;
if (typeof window !== "undefined") {
    window.addEventListener("pagehide", () => {
        isPageUnloading = true;
    });
}

function getHeaders(isFileUpload?: boolean): HeadersInit {
    const headers: Record<string, string> = isFileUpload
        ? {}
        : {
              "Content-Type": "application/json"
          };
    if (jwtToken && jwtToken != "" && jwtToken != undefined) {
        headers["Authorization"] = `Bearer ${jwtToken}`;
    }
    return headers;
}

function storeLatestURI() {
    const url = new URL(window.location.href);
    localStorage.setItem(
        "redirectAfterLogin",
        JSON.stringify({
            pathname: url.pathname,
            searchParams: Object.fromEntries(url.searchParams.entries())
        })
    );
}

export async function chatApi(request: ChatAppRequest, appType: string, corpId: string, abortSignal?: AbortSignal): Promise<Response | any> {
    return validateTokens("chat", "POST", JSON.stringify({ request: request, appType: appType, corpId: corpId }), abortSignal);
}

export async function chatPrecheckApi(request: ChatAppRequest, appType: string, corpId: string, abortSignal?: AbortSignal): Promise<Response | any> {
    return chatApi(request, appType, corpId, abortSignal);
}
export async function feedbackApi(options: SaveResponse, chat_id: string | undefined, appType: string): Promise<Response | any> {
    return validateTokens(
        "save-feedback",
        "POST",
        JSON.stringify({
            feedbacks: options.feedbacks,
            chat_id: chat_id,
            appType: appType
        })
    );
}

export async function loadSharedChatApi(sessionId: string, corpcode: string, sourcePage: string): Promise<Response | any> {
    return validateTokens(
        "load-shared-chat",
        "POST",
        JSON.stringify({
            group_id: sessionId,
            corpcode: corpcode,
            sourcePage: sourcePage
        })
    );
}
export async function loadChatApi(sessionId: unknown, sourcePage: string): Promise<Response | any> {
    return validateTokens(
        "load-chat",
        "POST",
        JSON.stringify({
            group_id: sessionId,
            sourcePage: sourcePage
        })
    );
}

export async function loadSessionSummaryApi(sessionId: string): Promise<Response | any> {
    return validateTokens(
        "load-session-summary",
        "POST",
        JSON.stringify({
            group_id: sessionId
        })
    );
}

export async function regenerateSessionSummaryApi(sessionId: string, locale?: string): Promise<Response | any> {
    return validateTokens(
        "regenerate-session-summary",
        "POST",
        JSON.stringify({
            group_id: sessionId,
            ...(locale ? { locale } : {})
        })
    );
}

export async function getThoughtsApi(conv_id: string, sourcePage: string = "chat"): Promise<Response | any> {
    return validateTokens("get-thoughts", "POST", JSON.stringify({ conv_id, sourcePage }));
}

export async function loadUsersApi(): Promise<Response | any> {
    return validateTokens("load-users", "POST", JSON.stringify({}));
}

export async function loadUserDataApi(): Promise<Response | any> {
    return validateTokens("load-user-data", "POST", JSON.stringify({}));
}

export async function deleteUserDataApi(corpId: string, userIds: string[]): Promise<Response | any> {
    return validateTokens("delete-user-data", "POST", JSON.stringify({ corpId, userIds }));
}

export async function deleteCorpDataApi(corpIds: string[]): Promise<Response | any> {
    return validateTokens("delete-corp-data", "POST", JSON.stringify({ corpIds }));
}

export async function loadSessionApi(nextDateToLoad: string, searchTerm: string): Promise<Response | any> {
    return validateTokens("load-sessions", "POST", JSON.stringify({ nextDate: nextDateToLoad, searchTerm: searchTerm }));
}

export async function fetchUserDataApi(selectedCorpIds: string[]): Promise<Response | any> {
    return validateTokens("fetch-userdata", "POST", JSON.stringify({ corpIds: selectedCorpIds }));
}

export async function loadFilteredSessionsApi(filters: object): Promise<Response | any> {
    return validateTokens(
        "load-filtered",
        "POST",
        JSON.stringify({
            filters: filters
        })
    );
}

export async function loadMetricsDataApi(): Promise<Response | any> {
    return validateTokens("load-metrics", "POST", JSON.stringify({}));
}

export async function updateMetricsDataApi(): Promise<Response | any> {
    return validateTokens("update-metrics", "POST", JSON.stringify({}));
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
}

/** Returns a URL to stream a PDF-extracted image blob directly, bypassing the citation path-rewriting logic. */
export function getPdfImageFilePath(blobPath: string): string {
    const encodedPath = blobPath
        .split("/")
        .map(segment => encodeURIComponent(segment))
        .join("/");
    return `${BACKEND_URI}/content/pdf-image/${encodedPath}`;
}

export function getUserContentFilePath(contentPath: string): string {
    return `${BACKEND_URI}/content/user-files/${contentPath}`;
}

export async function fetchUserContentFile(contentPath: string): Promise<Response | any> {
    return validateTokens(`content/user-files/${contentPath}`, "GET", null);
}

export async function saveUserDetails(settings: object): Promise<Response | any> {
    return validateTokens("save-user-details", "POST", JSON.stringify(settings));
}
export async function fetchAppInfo(): Promise<Response | any> {
    const queryParams = new URLSearchParams();
    queryParams.append("product_list", JSON.stringify([]));
    queryParams.append("load_products", JSON.stringify(false));
    return validateTokens("load-app-info?" + queryParams, "GET", JSON.stringify({}));
}

export async function saveAppSettings(appInfo: string): Promise<Response | any> {
    return validateTokens(
        "save-settings",
        "POST",
        JSON.stringify({
            appInfo: appInfo
        })
    );
}

export async function fetchProductInstructions(): Promise<Response | any> {
    return validateTokens("product-instructions", "GET", JSON.stringify({}));
}

export async function fetchCustomerPtfVersions(): Promise<Response | any> {
    return validateTokens("customer-ptf-versions", "GET", JSON.stringify({}));
}

export async function saveProductInstruction(instruction: unknown): Promise<Response | any> {
    return validateTokens("product-instructions", "POST", JSON.stringify(instruction));
}

export async function deleteProductInstruction(id: string, productIndex: string): Promise<Response | any> {
    return validateTokens("product-instructions", "DELETE", JSON.stringify({ id, product_index: productIndex }));
}

export async function fetchUpdateInfo(): Promise<Response | any> {
    return validateTokens("load-update-info", "GET", JSON.stringify({}));
}

export async function saveAppUpdates(updates: string): Promise<Response | any> {
    return validateTokens(
        "save-updates",
        "POST",
        JSON.stringify({
            updates: updates
        })
    );
}

export async function saveSharedChatValueApi(sessionValue: string, corp_id: string): Promise<Response | any> {
    return validateTokens(
        "save-shared-chat-value",
        "POST",
        JSON.stringify({
            group_id: sessionValue,
            corpcode: corp_id
        })
    );
}

export async function deleteSessionApi(sessionId: string): Promise<Response | any> {
    return validateTokens(
        "delete-session",
        "POST",
        JSON.stringify({
            session_id: sessionId
        })
    );
}

export async function saveTermsAcceptance(): Promise<Response | any> {
    return validateTokens("save-terms-accept", "POST", JSON.stringify({}));
}

export async function saveTemplate(template: object, product: string): Promise<Response> {
    return validateTokens(
        "save-template",
        "POST",
        JSON.stringify({
            template: template,
            product: product
        })
    );
}

export async function loadTemplate(product: string): Promise<Response> {
    return validateTokens("load-template", "POST", JSON.stringify({ product: product }));
}

export async function loadUserInfo(): Promise<Response> {
    return validateTokens("load-user-info", "POST", JSON.stringify({}));
}

export async function updateTemplate(id: string, pk: string, template: object, product: string, useCount: number): Promise<Response> {
    return validateTokens(
        "update-template",
        "POST",
        JSON.stringify({
            id: id,
            pk: pk,
            template: template,
            product: product,
            useCount: useCount
        })
    );
}

export async function deleteTemplate(id: string, product?: string): Promise<Response> {
    return validateTokens(
        "delete-template",
        "POST",
        JSON.stringify({
            id: id,
            product: product
        })
    );
}

export async function makeFavouriteTemplate(product: string, newId: string, action: string): Promise<Response> {
    return validateTokens(
        "make-favourite-template",
        "POST",
        JSON.stringify({
            product: product,
            newId: newId,
            action: action
        })
    );
}

export async function makeUserFavouriteTemplate(templateId: string, product: string, isDeleted?: boolean): Promise<Response> {
    return validateTokens(
        "make-user-favourite-template",
        "POST",
        JSON.stringify({
            product: product,
            templateId: templateId,
            isDeleted: isDeleted
        })
    );
}

export async function saveUserDataApi(userDetails: string): Promise<Response | any> {
    return validateTokens("save-user-data", "POST", userDetails);
}

export const saveMasterSettings = async (payload: any) => {
    return validateTokens("save-master-settings", "POST", JSON.stringify(payload));
};

async function validateTokens(
    endPoint: string,
    methodType: string,
    body: BodyInit | FormData | null | undefined,
    abortSignal?: AbortSignal,
    isFileUpload?: boolean
): Promise<Response | any> {
    try {
        if (jwtToken && jwtToken != "" && jwtToken != undefined) {
            const tokenValues = jwtDecode(jwtToken ?? "") as TokenValues;
            if (tokenValues?.exp < Date.now() / 1000) {
                alert(getAuthLabel("sessionExpired"));
                localStorage.removeItem("disclaimerHide");
                storeLatestURI();
                window.location.href = supportEnvHost;
                return JSON.stringify({ error: "token_expired" });
            }
        } else {
            alert(getAuthLabel("invalidSession"));
            window.location.href = supportEnvHost;
            storeLatestURI();
            return JSON.stringify({ error: "token_unavailable" });
        }
        let response: any;
        if (methodType === "GET") {
            response = await fetch(`/${endPoint}`, {
                method: methodType,
                headers: getHeaders()
            });
        } else {
            let requestBody = null;
            if ((endPoint === "chat" || endPoint === "upload") && methodType === "POST") {
                requestBody = {
                    method: methodType,
                    headers: getHeaders(isFileUpload),
                    body: body,
                    signal: abortSignal
                };
            } else {
                requestBody = {
                    method: methodType,
                    headers: getHeaders(),
                    body: body
                };
            }
            response = await fetch(`/${endPoint}`, requestBody);
        }
        if (!response?.ok) {
            const result = await response.json();
            if (result?.error_code) {
                alert(`${result?.error}. ${getAuthLabel("pleaseLoginAgain")}`);
                storeLatestURI();
                window.location.href = supportEnvHost;
            }
        }
        return response;
    } catch (error) {
        console.log(error);
        const isAbortError =
            error === "Abort Active Chat API Request" ||
            (error instanceof Error && (error.name === "AbortError" || error.message === "Abort Active Chat API Request"));
        if (isAbortError) {
            throw error;
        }
        if (isPageUnloading) {
            throw new DOMException("Page is unloading", "AbortError");
        }
        alert(getAuthLabel("requestError"));
        return;
    }
}

export const updateAppTypeValueInSessionStorage = async (appType: string) => {
    try {
        if (sessionStorage.getItem("SelectedAppType") !== appType) {
            const allowAll = appInfo?.use_all_tab === true;
            const appTypeValue = appType === "ALL" && !allowAll ? "FAQ" : isValidAppType(appType) ? appType : allowAll ? "ALL" : "FAQ";
            sessionStorage.setItem("SelectedAppType", appTypeValue);
            userInfo.apptype = appTypeValue;
            await saveUserDetails({ apptype: appTypeValue })
                .then(response => {
                    return response.json();
                })
                .catch(error => {
                    console.error("Error saving user data:", error);
                });
        }
    } catch (error) {
        console.error("Error saving user data:", error);
    }
};

export const getAppTypeBasedOnLastAnswerIsRelatedToAriel = (
    latestAnswersData: [user: QuestionDetails, response: { FAQ?: ChatAppResponse; Manuals?: ChatAppResponse; ALL?: ChatAppResponse }][]
): string => {
    // this method is called only for the history session load.
    const totalQuestionsCount = latestAnswersData?.length;
    const lastAnswerValue = latestAnswersData[totalQuestionsCount > 0 ? totalQuestionsCount - 1 : 0]?.[1];
    const lastAnswerQuestionDetails = latestAnswersData[totalQuestionsCount > 0 ? totalQuestionsCount - 1 : 0]?.[0];
    let appType: AppType = userInfo.apptype;
    // default manual apptype to load products.
    const defaultManualProducts = appInfo?.default_manual_products;
    if (lastAnswerValue["FAQ"] && !lastAnswerValue["Manuals"] && !lastAnswerValue["ALL"]) {
        appType = "FAQ";
    } else if (!lastAnswerValue["FAQ"] && !lastAnswerValue["ALL"] && lastAnswerValue["Manuals"]) {
        appType = "Manuals";
    } else {
        appType = lastAnswerQuestionDetails?.combinedAppTypeResponse ? "ALL" : "FAQ";
    }
    const resolvedAppType = defaultManualProducts.some((productIndexValue: string) =>
        appType === "ALL"
            ? productIndexValue === lastAnswerValue[appType]?.context?.product_filter?.faq_key ||
              productIndexValue === lastAnswerValue[appType]?.context?.product_filter?.manual_key
            : productIndexValue ===
              (appType === "FAQ" ? lastAnswerValue[appType]?.context?.product_filter?.faq_key : lastAnswerValue[appType]?.context?.product_filter?.manual_key)
    )
        ? lastAnswerQuestionDetails?.combinedAppTypeResponse
            ? "ALL"
            : "Manuals"
        : lastAnswerQuestionDetails?.combinedAppTypeResponse
          ? "ALL"
          : "ALL"; //userInfo.apptype;
    return appInfo?.use_all_tab === true ? resolvedAppType : resolvedAppType === "ALL" ? "FAQ" : resolvedAppType;
};

export const getPromptInstructionsToRender = (template: any): string => {
    if (
        !!(template && template !== "" && template.hasOwnProperty("promptInstructions") && template?.promptInstructions && template?.promptInstructions !== "")
    ) {
        const promptInstructionsText = template?.promptInstructions;
        const promptInstructionsTextArray = promptInstructionsText?.trim().split("\n");
        const nonEmptyLines = promptInstructionsTextArray?.filter((line: string) => line.trim() !== "");
        const isInstructionsLabelExist = String(nonEmptyLines[0]).includes(InstructionLabel.EN) || String(nonEmptyLines[0]).includes(InstructionLabel.JA);
        return isInstructionsLabelExist
            ? "\n\n" + promptInstructionsText
            : !!(promptInstructionsText && promptInstructionsText !== "")
              ? (sessionStorage.getItem("selectedLocale") as Locale) === Locale.EN
                  ? "\n\n" + InstructionLabel.EN + ": \n\n" + promptInstructionsText
                  : "\n\n" + InstructionLabel.JA + ": \n\n" + promptInstructionsText
              : "";
    }
    return "";
};

export const getChatSummaryTitle = (questionInputTextValue: string, templateIdValue: string, templateContents: any): string => {
    const filteredTemplateValue = templateContents
        ?.filter((tempContent: any) => tempContent.id === templateIdValue)
        .map((tempContent: any) => {
            return tempContent.template[0];
        })[0];

    const templatePrompt = String(filteredTemplateValue?.prompt).trim();
    const diff = diffWords(templatePrompt, questionInputTextValue);
    const extraTextLines = diff
        .filter(part => part.added)
        .map(part => part.value)
        .join("");

    return filteredTemplateValue?.isSystemTemplate === false
        ? questionInputTextValue
        : extraTextLines && extraTextLines !== ""
          ? extraTextLines
          : questionInputTextValue;
};

export async function cancelChatApiRequestProcessingOnServerSide(options?: {
    edited_question_id?: string | null;
    is_edit_request?: boolean;
    is_chat_shared?: boolean;
    sessionId?: string | null;
}): Promise<Response | any> {
    return validateTokens(
        "cancel",
        "POST",
        JSON.stringify({
            edited_question_id: options?.edited_question_id ?? null,
            is_edit_request: options?.is_edit_request === true,
            is_chat_shared: options?.is_chat_shared === true,
            sessionId: options?.sessionId
        })
    );
}

export async function getSessionInfoData(sessionIdValue: string): Promise<Response | any> {
    return validateTokens("load-selected-sessions", "POST", JSON.stringify({ session_id: sessionIdValue }));
}

export const highlightTextAsHtml = (text: string | undefined, search: string): string => {
    if (!search.trim() || !text) return text ?? "";
    const terms = search
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(term => escapeRegExp(term));
    const pattern = new RegExp(`(${terms.join("|")})`, "gi");
    return text?.replace(pattern, match => `<mark>${match}</mark>`);
};

type VersionEntry = unknown;
type VersionMap = Record<string, VersionEntry>;
type HistoryRecords = Record<string, VersionMap>;

/**
 * Calculates the next version number for a given history record.
 * Assumes all keys in the versions object are numeric strings (e.g., "0", "1", ...).
 * If non-numeric keys are present, they will be ignored in the calculation.
 * Consider validating your data to ensure only numeric keys are used.
 * Returns the total number of versions found in the history record.
 */
export function calculateVersions(histories: HistoryRecords | undefined, idToCheck?: string): number {
    const versions = idToCheck ? histories?.[idToCheck] : undefined;
    if (!versions) return 1;
    const numericKeys = Object.keys(versions)
        .map(k => Number(k))
        .filter(n => Number.isFinite(n));
    if (numericKeys.length === 0) return 1;
    return Math.max(...numericKeys) + 1;
}

export const getIdToCheck = (currentQuestionInfo: any) =>
    currentQuestionInfo?.parentMessageId && currentQuestionInfo.parentMessageId !== "0"
        ? String(currentQuestionInfo.parentMessageId)
        : String(currentQuestionInfo?.question_id ?? "");
const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export async function UploadFile(formData: FormData): Promise<any> {
    return validateTokens("upload", "POST", formData, undefined, true);
}

export async function deleteFile(removeFileAttachments: any): Promise<any> {
    return validateTokens("delete-file", "POST", JSON.stringify({ file_attachments: removeFileAttachments }));
}

// Best-effort cleanup for unload/refresh where normal fetches may be canceled.
export function deleteFileOnUnload(removeFileAttachments: any): void {
    try {
        const body = JSON.stringify({ file_attachments: removeFileAttachments });
        fetch("/delete-file", {
            method: "POST",
            headers: getHeaders(),
            body,
            keepalive: true
        });
    } catch {
        // ignore unload-time failures
    }
}

export async function getFileAttachmentServerSideIds(questionId: Array<string>): Promise<any> {
    return validateTokens("get-attachment-ids", "POST", JSON.stringify({ question_id: questionId }));
}

export const saveUserSettings = async (settings: object) => {
    try {
        const response = await saveUserDetails(settings);
        await response.json();
    } catch (error) {
        console.error("Error saving user data:", error);
    }
};

type ID = string;

type AnswerRow = [QuestionRow, any];

interface QuestionRow {
    question_id: ID;
    parentMessageId?: ID | null;
    edited_version?: number | string | null;
    [k: string]: any;
}

type Answers = AnswerRow[];
type Histories = Record<ID, Record<string | number, AnswerRow[]>>;

export interface TreeNode {
    record: { question: QuestionRow; answer: any };
    question_id: ID;
    parentMessageId?: ID | null;
    children: TreeNode[];
}

// ---- tree indexing
function indexTreeById(forest: TreeNode[]): Map<ID, TreeNode> {
    const map = new Map<ID, TreeNode>();
    const walk = (node: TreeNode) => {
        if (!map.has(node.question_id)) map.set(node.question_id, node);
        node.children.forEach(walk);
    };
    forest.forEach(walk);
    return map;
}

// ---- build full tree from answers + histories (safe default)
export function buildTree(answers: Answers, histories: Histories): TreeNode[] {
    const expand = (question: QuestionRow, answer: any, seen: Set<ID>): TreeNode => {
        const node: TreeNode = {
            record: { question, answer },
            question_id: question.question_id,
            parentMessageId: question.parentMessageId ?? null,
            children: []
        };

        const bucket = histories[question.question_id];
        if (!bucket || seen.has(question.question_id)) return node;
        seen.add(question.question_id);

        const versionKeys = Object.keys(bucket).sort((a, b) => {
            const na = Number(a),
                nb = Number(b);
            const aNum = !Number.isNaN(na),
                bNum = !Number.isNaN(nb);
            if (aNum && bNum) return na - nb;
            if (aNum && !bNum) return -1;
            if (!aNum && bNum) return 1;
            return a.localeCompare(b);
        });

        const kids: TreeNode[] = [];
        for (const version of versionKeys) {
            const rows = bucket[version] ?? [];
            for (const [childQuestion, childAnswer] of rows) {
                if (!childQuestion?.question_id) continue;
                kids.push(expand(childQuestion, childAnswer, seen));
            }
        }
        // de-dupe by question_id
        const seenChild = new Set<string>();
        const dedupedChildren: TreeNode[] = [];
        for (const child of kids) {
            if (!seenChild.has(child.question_id)) {
                seenChild.add(child.question_id);
                dedupedChildren.push(child);
            }
        }
        node.children = dedupedChildren;
        seen.delete(question.question_id);
        return node;
    };

    return answers.map(([question, answer]) => (question?.question_id ? expand(question, answer, new Set<ID>()) : null)).filter(Boolean) as TreeNode[];
}

// ---- load rows for a given (parent, version) from tree
export function loadAnswerRowsFromTree(answers: any, answerIndex: number, forest: TreeNode[], parentMessageId: ID, edited_version: number): AnswerRow[] {
    const byId = indexTreeById(forest);
    const parent = byId.get(parentMessageId);
    if (!parent) return [];

    // Collect all child matches
    const rows: AnswerRow[] = parent.children
        .filter(child => child.record.question.edited_version === edited_version)
        .map<AnswerRow>(child => [child.record.question, child.record.answer]);

    // If the parent’s question_id matches the parentMessageId, add it at index 0
    if (edited_version === 0 && parent.record.question.question_id === parentMessageId) {
        // For edited_version === 0, if the parent question matches the parentMessageId,
        // and has a rootId, we want to load all rows for that root/version.
        // Otherwise, just add the parent row itself.
        if (parent.record.question.rootId === "") {
            return forest.map<any>((root: any) => [root.record.question, root.record.answer]);
        }
        return loadAnswerRowsByRootAndVersion(forest, parent.record.question.rootId, parent.record.question.edited_version ?? 0);
    }
    const base: any = answerIndex >= 0 ? answers.slice(0, answerIndex) : [];
    return [...base, ...rows];
}

export function loadAnswerRowsByRootAndVersion(forest: TreeNode[], rootId: ID, editedVersion: number | string): AnswerRow[] {
    const rows: AnswerRow[] = [];
    const sameVersion = (a: unknown, b: unknown) => String(a ?? 0) === String(b ?? 0);
    const visit = (node: TreeNode) => {
        const { question, answer } = node.record;
        if (question.question_id === rootId) {
            for (const child of node.children) {
                const childQuestion = child.record.question;
                const childAnswer = child.record.answer;
                const row: AnswerRow = [childQuestion, childAnswer];
                if (childQuestion.rootId === rootId && sameVersion(childQuestion.edited_version, editedVersion)) {
                    rows.push(row);
                }
            }
        }
        for (const child of node.children) visit(child);
    };
    for (const tree of forest) visit(tree);
    rows.sort((a, b) => {
        const orderA = a[0].question_order ?? 0;
        const orderB = b[0].question_order ?? 0;
        return orderA - orderB;
    });
    return rows;
}

// ---- immutable histories insert
export function insertIntoHistories(histories: Histories, parentMessageId: ID, edited_version: number | string, row: AnswerRow): any {
    const [question] = row;
    const childQuestion = { ...question, parentMessageId, edited_version };
    const fixedRow: AnswerRow = [childQuestion, row[1]];

    const newHistories: Histories = { ...histories };
    const bucket = newHistories[parentMessageId] ? { ...newHistories[parentMessageId] } : {};
    const key = edited_version;
    const arr = Array.isArray(bucket[key]) ? [...bucket[key]] : [];
    arr.push(fixedRow);
    bucket[key] = arr;
    newHistories[parentMessageId] = bucket;
    return newHistories;
}

// ---- immutable, targeted insert into an existing tree (no full rebuild)
export function insertRowIntoTree(forest: TreeNode[], parentMessageId: ID | null | undefined, row: AnswerRow): TreeNode[] {
    const [question, answer] = row;
    const newNode: TreeNode = {
        record: { question, answer },
        question_id: question.question_id,
        parentMessageId: question.parentMessageId ?? null,
        children: []
    };

    // No parent -> add as root
    if (!parentMessageId) return [...forest, newNode];

    const byId = indexTreeById(forest);
    const parent = byId.get(parentMessageId);
    if (!parent) {
        // parent missing -> treat as root
        return [...forest, newNode];
    }

    // clone only the branch owning the parent
    const addChild = (nodes: TreeNode[]): TreeNode[] =>
        nodes.map(node => (node === parent ? { ...node, children: [...node.children, newNode] } : { ...node, children: addChild(node.children) }));

    // find owner root
    const owns = (root: TreeNode): boolean => {
        if (root === parent) return true;
        const stack = [...root.children];
        while (stack.length) {
            const cur = stack.pop()!;
            if (cur === parent) return true;
            stack.push(...cur.children);
        }
        return false;
    };
    return forest.map(r => (owns(r) ? addChild([r])[0] : r));
}

export function updateAnswersByIndex(answers: Answers, index: number, questionId: ID, key: string, value: any, questionPatch?: Partial<QuestionRow>): Answers {
    if (index < 0 || index >= answers.length) return answers;
    const [question, answer] = answers[index] ?? [];
    if (!question || question.question_id !== questionId) return answers;

    const newQuestion = questionPatch ? { ...question, ...questionPatch } : question;
    const newAnswer = { ...(answer ?? {}), [key]: value };

    // no change? return same ref
    if (answer && answer[key] === value && !questionPatch) return answers;

    const next = answers.slice();
    next[index] = [newQuestion, newAnswer];
    return next;
}

export function updateHistoriesByQuestionId(
    histories: Histories,
    questionId: ID,
    key: string,
    value: any,
    questionPatch?: Partial<QuestionRow>
): { histories: Histories; changed: boolean } {
    let changed = false;
    const next: Histories = { ...histories };

    for (const parentId of Object.keys(histories)) {
        const bucket = histories[parentId];
        if (!bucket) continue;

        let bucketChanged = false;
        const newBucket: Record<string | number, AnswerRow[]> = { ...bucket };

        for (const ver of Object.keys(bucket)) {
            const rows = bucket[ver] ?? [];
            let rowsChanged = false;

            const newRows = rows.map(([question, answer]) => {
                if (question?.question_id !== questionId) return [question, answer] as AnswerRow;
                rowsChanged = true;
                const newQuestion = questionPatch ? { ...question, ...questionPatch } : question;
                const newAnswer = { ...(answer ?? {}), [key]: value };
                return [newQuestion, newAnswer] as AnswerRow;
            });

            if (rowsChanged) {
                newBucket[ver] = newRows;
                bucketChanged = true;
            }
        }

        if (bucketChanged) {
            next[parentId] = newBucket;
            changed = true;
        }
    }

    return { histories: next, changed };
}

export function updateTreeAnswerByQuestionId(forest: TreeNode[], questionId: ID, key: string, value: any, questionPatch?: Partial<QuestionRow>): TreeNode[] {
    const updateNode = (node: TreeNode): [TreeNode, boolean] => {
        let did = false;

        // children first
        let kidsChanged = false;
        const kids = node.children.map(ch => {
            const [upd, childDid] = updateNode(ch);
            if (childDid) kidsChanged = true;
            return upd;
        });

        let newNode = node;

        // this node
        if (node.question_id === questionId) {
            const newQ = questionPatch ? { ...node.record.question, ...questionPatch } : node.record.question;
            const newA = { ...(node.record.answer ?? {}), [key]: value };
            newNode = { ...node, record: { question: newQ, answer: newA } };
            did = true;
        }

        if (kidsChanged) {
            newNode = newNode === node ? { ...node, children: kids } : { ...newNode, children: kids };
            did = true;
        }

        return [newNode, did];
    };

    let any = false;
    const out = forest.map(r => {
        const [n, did] = updateNode(r);
        if (did) any = true;
        return n;
    });

    return any ? out : forest;
}

export function updateEverywhere(
    input: {
        answers: Answers;
        histories: Histories;
        tree: TreeNode[];
    },
    params: {
        index: number; // answers index to update
        questionId: ID; // must match answers[index][0].question_id
        key: string; // property inside the answer payload to set
        value: any; // new value
        questionPatch?: Partial<QuestionRow>; // optional fields to patch on question
    }
) {
    const { answers, histories, tree } = input;
    const { index, questionId, key, value, questionPatch } = params;

    const nextAnswers = updateAnswersByIndex(answers, index, questionId, key, value, questionPatch);
    const { histories: nextHistories } = updateHistoriesByQuestionId(histories, questionId, key, value, questionPatch);
    const nextTree = updateTreeAnswerByQuestionId(tree, questionId, key, value, questionPatch);

    return { answers: nextAnswers, histories: nextHistories, tree: nextTree };
}

const UNWANTED_TAGS = ["marquee", "script", "iframe", "object", "embed", "video", "audio", "base", "form", "input", "meta", "style", "ocrtext"] as const;

const encodeUnwantedTags = (html: string, tags = UNWANTED_TAGS) => {
    if (!html) return "";

    return tags.reduce((content, tag) => {
        const fullTagRegex = new RegExp(`</?${tag}\\b[^>]*>`, "gi");
        const partialOpenRegex = new RegExp(`<${tag}\\b(?=[^>]*$|[^>]*\\s)`, "gi");
        const partialCloseRegex = new RegExp(`</${tag}\\b(?=[^>]*$|[^>]*\\s)`, "gi");
        content = content.replace(fullTagRegex, m => m.replace(/</g, "&lt;").replace(/>/g, "&gt;"));
        content = content.replace(partialOpenRegex, m => m.replace(/</g, "&lt;"));
        content = content.replace(partialCloseRegex, m => m.replace(/</g, "&lt;"));
        return content;
    }, html);
};

export function decodeHtmlEntities(value: string) {
    return value.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}

export function escapeHtml(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#x27;");
}

export function skipUnwantedTag(htmlToSanitize: string | undefined) {
    if (!htmlToSanitize || htmlToSanitize === "") {
        return "";
    }
    const escaped = encodeUnwantedTags(htmlToSanitize);
    return DOMPurify.sanitize(escaped, {
        KEEP_CONTENT: true,
        FORBID_ATTR: [
            "onclick",
            "onload",
            "onerror",
            "onmouseover",
            "onfocus",
            "onblur",
            "oninput",
            "onchange",
            "onsubmit",
            "onreset",
            "onselect",
            "onunload",
            "onabort",
            "onkeypress",
            "onkeydown",
            "onkeyup"
        ]
    });
}
